import { useEffect, useMemo, useState } from 'react';
import { useRegisterAppAssistantContext } from '@/lib/app-assistant-context';
import { supabase } from '@/lib/supabase';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Badge } from '@/components/ui/Badge';
import {
  createKnowledgeFolder,
  deleteKnowledgeFolder,
  listKnowledgeFolders,
  type KnowledgeFolderWithCount,
} from '@/lib/knowledge';
import { KnowledgeFolderDetails } from './KnowledgeFolderDetails';

interface KnowledgeFoldersSectionProps {
  userId: string;
  tenantId: string | null;
  isManager: boolean;
}

export const KnowledgeFoldersSection = ({
  userId,
  tenantId,
  isManager,
}: KnowledgeFoldersSectionProps) => {
  const [folders, setFolders] = useState<KnowledgeFolderWithCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const assistantContext = useMemo(() => ({
    surface: 'home' as const, routePath: '/profil', routeLabel: 'Profil › Documents',
    visibleSummary: loading ? 'Documents en cours de chargement.'
      : `Dossiers de documents (citables dans le chat avec @) : ${folders.length}.`,
    visibleEntities: folders.map((folder) => folder.name).slice(0, 12),
  }), [folders, loading]);
  useRegisterAppAssistantContext(assistantContext);

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState<'personal' | 'shared'>('personal');
  const [submitting, setSubmitting] = useState(false);

  const [expanded, setExpanded] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listKnowledgeFolders();
      setFolders(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const handleCreate = async () => {
    if (!tenantId) {
      setError('Tenant indisponible — réessaie dans un instant.');
      return;
    }
    if (!name.trim()) {
      setError('Donne un nom au dossier.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await createKnowledgeFolder({
        name: name.trim(),
        description: description.trim() || undefined,
        kind,
        tenantId,
        userId,
      });
      setName('');
      setDescription('');
      setKind('personal');
      setCreating(false);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Création impossible');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (folderId: string) => {
    if (!confirm('Supprimer ce dossier et tous ses documents ?')) return;
    try {
      await deleteKnowledgeFolder(folderId);
      if (expanded === folderId) setExpanded(null);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Suppression impossible');
    }
  };

  const adjustCount = (folderId: string, delta: number) => {
    setFolders((current) =>
      current.map((f) =>
        f.id === folderId ? { ...f, document_count: Math.max(0, f.document_count + delta) } : f,
      ),
    );
  };

  const canEditFolder = (folder: KnowledgeFolderWithCount): boolean => {
    if (folder.kind === 'personal') return folder.owner_user_id === userId;
    return isManager;
  };

  return (
    <div className="mt-10">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Dossiers de connaissance</h2>
          <p className="text-xs text-gray-500 mt-1">
            Documents réutilisables qu'un tool peut consulter à l'exécution (recherche par mot-clé).
          </p>
        </div>
        {!creating ? (
          <Button size="sm" variant="secondary" onClick={() => setCreating(true)}>
            + Nouveau dossier
          </Button>
        ) : null}
      </div>

      {creating ? (
        <Card className="mb-4 space-y-3">
          <Input
            placeholder="Nom du dossier (ex: Doc commerciale)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <Textarea
            placeholder="À quoi sert ce dossier ? (optionnel)"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setKind('personal')}
              className={`text-xs px-3 py-1.5 rounded-md border ${
                kind === 'personal'
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-700 border-gray-200'
              }`}
            >
              Personnel
            </button>
            <button
              type="button"
              disabled={!isManager}
              onClick={() => setKind('shared')}
              className={`text-xs px-3 py-1.5 rounded-md border ${
                kind === 'shared'
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-700 border-gray-200'
              } ${!isManager ? 'opacity-40 cursor-not-allowed' : ''}`}
              title={isManager ? '' : 'Réservé aux managers'}
            >
              Partagé tenant
            </button>
          </div>
          {error ? <div className="text-xs text-red-600">{error}</div> : null}
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => void handleCreate()} loading={submitting} disabled={submitting}>
              Créer le dossier
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setCreating(false);
                setError(null);
              }}
              disabled={submitting}
            >
              Annuler
            </Button>
          </div>
        </Card>
      ) : null}

      {loading ? (
        <div className="text-xs text-gray-400">Chargement des dossiers…</div>
      ) : folders.length === 0 ? (
        <Card className="text-sm text-gray-500 text-center py-6">
          Aucun dossier de connaissance pour l'instant.
        </Card>
      ) : (
        <div className="space-y-2">
          {folders.map((folder) => {
            const isOpen = expanded === folder.id;
            const editable = canEditFolder(folder);
            return (
              <Card key={folder.id} className="!p-0">
                <div className="flex items-start gap-3 p-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">{folder.name}</h3>
                      <Badge variant={folder.kind === 'shared' ? 'success' : 'default'}>
                        {folder.kind === 'shared' ? 'Partagé' : 'Personnel'}
                      </Badge>
                    </div>
                    {folder.description ? (
                      <p className="text-xs text-gray-500 mt-1">{folder.description}</p>
                    ) : null}
                    <div className="text-[11px] text-gray-400 mt-1">
                      {folder.document_count} document{folder.document_count > 1 ? 's' : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setExpanded(isOpen ? null : folder.id)}
                    >
                      {isOpen ? 'Replier' : 'Ouvrir'}
                    </Button>
                    {editable ? (
                      <button
                        type="button"
                        onClick={() => void handleDelete(folder.id)}
                        className="text-xs text-gray-400 hover:text-red-600"
                      >
                        Supprimer
                      </button>
                    ) : null}
                  </div>
                </div>
                {isOpen ? (
                  <div className="px-4 pb-4">
                    {editable ? (
                      <KnowledgeFolderDetails
                        folderId={folder.id}
                        folderName={folder.name}
                        userId={userId}
                        onDocumentCountChange={(delta) => adjustCount(folder.id, delta)}
                      />
                    ) : (
                      <div className="text-xs text-gray-500 italic px-1">
                        Lecture seule — ce dossier partagé est géré par un manager.
                      </div>
                    )}
                  </div>
                ) : null}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Resolve current user + tenant for the section. Lives next to the component so the section
 * can render even if the parent page hasn't fetched membership yet.
 */
export const useKnowledgeIdentity = () => {
  const [userId, setUserId] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [isManager, setIsManager] = useState(false);
  // The role decides what a page shows: it waits (skeleton) until the role is known.
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (cancelled) return;
      if (!user) { setReady(true); return; }
      setUserId(user.id);
      const { data: membership } = await supabase
        .from('tenant_members')
        .select('tenant_id, tenant_role')
        .eq('user_id', user.id)
        .maybeSingle();
      if (cancelled) return;
      if (membership) {
        setTenantId(membership.tenant_id);
        setIsManager(membership.tenant_role === 'manager');
      }
      setReady(true);
    };
    void run().catch((cause: unknown) => {
      console.error('Identity lookup failed', cause instanceof Error ? cause.message : cause);
      if (!cancelled) setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { userId, tenantId, isManager, ready };
};
